import type { QueryClient } from "@tanstack/react-query";

import type { MrpIncomingRow } from "@/lib/mrp";

import type { KittingPickLine } from "./kittingPickModel";
import {
  kittingPickDataQueryKey,
  type KittingPickDataApiResponse,
} from "./kittingPickDataApi";
import {
  kittingCurrentPickRunQueryKey,
  kittingPickRunQueryKey,
  type KittingPickInventoryContainer,
  type KittingPickRun,
  type KittingPickRunActionResponse,
  type KittingPickRunCancelResponse,
  type KittingPickRunItem,
} from "./kittingPickRunApi";

export interface KittingPickActionCacheSnapshot {
  pickData: KittingPickDataApiResponse | undefined;
  run: KittingPickRun | undefined;
}

function kittingIncomingRowPatchKey(row: MrpIncomingRow): string {
  if (row.kind === "transfer_order") {
    return `transfer:${String(row.id)}:${row.transfer_role ?? ""}`;
  }
  return `work_order:${String(row.id)}`;
}

function isPartialKittingPickDataRefresh(refresh: KittingPickDataApiResponse): boolean {
  return (
    refresh.inventory_rows.length === 0 &&
    refresh.sublocation_rows.length === 0 &&
    Object.keys(refresh.sku_hover_hints).length === 0
  );
}

function mergeKittingPickIncomingRows(
  previous: readonly MrpIncomingRow[],
  patches: readonly MrpIncomingRow[],
): MrpIncomingRow[] {
  if (patches.length === 0) {
    return [...previous];
  }
  const merged = new Map(previous.map((row) => [kittingIncomingRowPatchKey(row), row]));
  for (const patch of patches) {
    const key = kittingIncomingRowPatchKey(patch);
    const remaining = Math.max(0, Math.round(Number(patch.quantity_remaining ?? 0)));
    const allocated = Math.max(0, Math.round(Number(patch.allocated_stock ?? 0)));
    if (remaining <= 0 && allocated <= 0) {
      merged.delete(key);
      continue;
    }
    merged.set(key, patch);
  }
  return Array.from(merged.values());
}

function mergeKittingPickDataRefresh(
  previous: KittingPickDataApiResponse | undefined,
  refresh: KittingPickDataApiResponse,
): KittingPickDataApiResponse {
  if (previous == null || !isPartialKittingPickDataRefresh(refresh)) {
    return refresh;
  }
  return {
    incoming_rows: mergeKittingPickIncomingRows(previous.incoming_rows, refresh.incoming_rows),
    inventory_rows: previous.inventory_rows,
    sublocation_rows: previous.sublocation_rows,
    sku_hover_hints: previous.sku_hover_hints,
  };
}

function normalizeKittingSku(sku: string): string {
  return sku.trim().toLowerCase();
}

function findKittingIncomingRowForPickLine(
  incomingRows: readonly MrpIncomingRow[],
  line: KittingPickLine,
): MrpIncomingRow | undefined {
  const workOrderId = line.job.workOrderId;
  if (workOrderId == null) {
    return undefined;
  }
  const sku = normalizeKittingSku(line.sku);
  return incomingRows.find(
    (row) =>
      row.kind === "work_order" &&
      Number(row.id) === workOrderId &&
      normalizeKittingSku(String(row.cpn ?? "")) === sku,
  );
}

/** Mirrors server max(BOM, virtual) display: pick increases allocated and decreases remaining. */
export function applyKittingPickQuantityDeltaToIncomingRow(
  row: MrpIncomingRow,
  quantityDelta: number,
): MrpIncomingRow {
  const delta = Math.trunc(quantityDelta);
  const remaining = Math.max(0, Math.round(Number(row.quantity_remaining ?? 0)) - delta);
  const allocated = Math.max(0, Math.round(Number(row.allocated_stock ?? 0)) + delta);
  return {
    ...row,
    quantity_remaining: remaining,
    allocated_stock: allocated,
  };
}

export function applyOptimisticKittingPickToPickData(
  pickData: KittingPickDataApiResponse,
  line: KittingPickLine,
  quantity: number,
): KittingPickDataApiResponse | null {
  const incomingRow = findKittingIncomingRowForPickLine(pickData.incoming_rows, line);
  if (incomingRow == null) {
    return null;
  }
  const patchedRow = applyKittingPickQuantityDeltaToIncomingRow(incomingRow, quantity);
  return {
    ...pickData,
    incoming_rows: pickData.incoming_rows.map((row) =>
      row === incomingRow ? patchedRow : row,
    ),
  };
}

function kittingRunItemKey(
  item: Pick<KittingPickRunItem, "slotKey" | "buildOrderId" | "transferOrderId" | "componentSku">,
): string {
  return `${item.slotKey}\0${String(item.buildOrderId)}\0${String(item.transferOrderId)}\0${normalizeKittingSku(item.componentSku)}`;
}

function mergeKittingPickInventoryContainers(
  existing: readonly KittingPickInventoryContainer[] | undefined,
  added: readonly KittingPickInventoryContainer[],
): KittingPickInventoryContainer[] {
  const seen = new Set((existing ?? []).map((container) => container.hex));
  const merged = [...(existing ?? [])];
  for (const container of added) {
    if (seen.has(container.hex)) continue;
    seen.add(container.hex);
    merged.push(container);
  }
  return merged;
}

export function applyOptimisticKittingPickToRun(
  run: KittingPickRun,
  line: KittingPickLine,
  quantity: number,
  containers: readonly KittingPickInventoryContainer[] = [],
): KittingPickRun {
  const buildOrderId = line.job.workOrderId ?? null;
  const transferOrderId = line.job.kind === "transfer_order" ? line.job.transferOrderId ?? null : null;
  if (buildOrderId == null && transferOrderId == null) {
    return run;
  }
  const delta = Math.trunc(quantity);
  const slotKey = line.destinationSlotId;
  const componentSku = line.sku.trim();
  const key = kittingRunItemKey({ slotKey, buildOrderId, transferOrderId, componentSku });
  const existingIndex = run.items.findIndex((item) => kittingRunItemKey(item) === key);

  if (existingIndex >= 0) {
    const existing = run.items[existingIndex]!;
    const pickedQuantity = existing.pickedQuantity + delta;
    const unabsorbedVirtual = Math.max(0, pickedQuantity - existing.absorbedQuantity);
    const items = [...run.items];
    items[existingIndex] = {
      ...existing,
      pickedQuantity,
      unabsorbedVirtual,
      inventoryContainers: mergeKittingPickInventoryContainers(existing.inventoryContainers, containers),
    };
    return { ...run, items };
  }

  const newItem: KittingPickRunItem = {
    id: -1,
    slotKey,
    buildOrderId,
    transferOrderId,
    componentSku,
    pickedQuantity: delta,
    absorbedQuantity: 0,
    unabsorbedVirtual: delta,
    inventoryContainers: [...containers],
  };
  return { ...run, items: [...run.items, newItem] };
}

export function applyOptimisticKittingReturnToRun(
  run: KittingPickRun,
  line: KittingPickLine,
  quantity: number,
  inventoryBoxHex?: string | null,
): KittingPickRun {
  const buildOrderId = line.job.workOrderId ?? null;
  const transferOrderId = line.job.kind === "transfer_order" ? line.job.transferOrderId ?? null : null;
  if (buildOrderId == null && transferOrderId == null) {
    return run;
  }
  const delta = Math.trunc(quantity);
  const key = kittingRunItemKey({
    slotKey: line.destinationSlotId,
    buildOrderId,
    transferOrderId,
    componentSku: line.sku,
  });
  const items = run.items
    .map((item) => {
      if (kittingRunItemKey(item) !== key) {
        return item;
      }
      const pickedQuantity = Math.max(0, item.pickedQuantity - delta);
      const unabsorbedVirtual = Math.max(0, pickedQuantity - item.absorbedQuantity);
      const hex = inventoryBoxHex?.trim().toLowerCase() ?? "";
      return {
        ...item,
        pickedQuantity,
        unabsorbedVirtual,
        inventoryContainers: hex === ""
          ? item.inventoryContainers
          : (item.inventoryContainers ?? []).filter((container) => container.hex.trim().toLowerCase() !== hex),
      };
    })
    .filter((item) => item.pickedQuantity > 0);
  return { ...run, items };
}

export function snapshotKittingPickActionCache(
  queryClient: QueryClient,
  slotIds: readonly string[],
  runId: number | null,
): KittingPickActionCacheSnapshot {
  return {
    pickData: queryClient.getQueryData<KittingPickDataApiResponse>(kittingPickDataQueryKey(slotIds)),
    run:
      runId != null
        ? queryClient.getQueryData<KittingPickRun>(kittingPickRunQueryKey(runId))
        : undefined,
  };
}

export function rollbackKittingPickActionCache(
  queryClient: QueryClient,
  slotIds: readonly string[],
  runId: number | null,
  snapshot: KittingPickActionCacheSnapshot,
): void {
  queryClient.setQueryData(kittingPickDataQueryKey(slotIds), snapshot.pickData);
  if (runId != null) {
    queryClient.setQueryData(kittingPickRunQueryKey(runId), snapshot.run);
  }
}

export function applyOptimisticKittingPickAction(
  queryClient: QueryClient,
  slotIds: readonly string[],
  runId: number,
  line: KittingPickLine,
  quantity: number,
  containers: readonly KittingPickInventoryContainer[] = [],
): void {
  queryClient.setQueryData<KittingPickDataApiResponse>(kittingPickDataQueryKey(slotIds), (previous) => {
    if (previous == null) {
      return previous;
    }
    return applyOptimisticKittingPickToPickData(previous, line, quantity) ?? previous;
  });
  queryClient.setQueryData<KittingPickRun>(kittingPickRunQueryKey(runId), (previous) => {
    if (previous == null) {
      return previous;
    }
    return applyOptimisticKittingPickToRun(previous, line, quantity, containers);
  });
}

export function applyOptimisticKittingReturnAction(
  queryClient: QueryClient,
  slotIds: readonly string[],
  runId: number,
  line: KittingPickLine,
  quantity: number,
  inventoryBoxHex?: string | null,
): void {
  queryClient.setQueryData<KittingPickDataApiResponse>(kittingPickDataQueryKey(slotIds), (previous) => {
    if (previous == null) {
      return previous;
    }
    return applyOptimisticKittingPickToPickData(previous, line, -quantity) ?? previous;
  });
  queryClient.setQueryData<KittingPickRun>(kittingPickRunQueryKey(runId), (previous) => {
    if (previous == null) {
      return previous;
    }
    return applyOptimisticKittingReturnToRun(previous, line, quantity, inventoryBoxHex);
  });
}

export function applyKittingPickActionResponse(
  queryClient: QueryClient,
  slotIds: readonly string[],
  runId: number | null,
  response: KittingPickRunActionResponse,
): void {
  queryClient.setQueryData<KittingPickDataApiResponse>(
    kittingPickDataQueryKey(slotIds),
    (previous) => mergeKittingPickDataRefresh(previous, response.pickData),
  );
  if (runId != null) {
    queryClient.setQueryData<KittingPickRun>(kittingPickRunQueryKey(runId), response.run);
  }
  void queryClient.invalidateQueries({ queryKey: kittingCurrentPickRunQueryKey(), refetchType: "none" });
}

export function applyKittingPickCancelResponse(
  queryClient: QueryClient,
  response: KittingPickRunCancelResponse,
): void {
  queryClient.setQueryData<KittingPickDataApiResponse>(
    kittingPickDataQueryKey(response.slotKeys),
    response.pickData,
  );
  queryClient.setQueryData(kittingCurrentPickRunQueryKey(), null);
}

export function kittingRunTotalPickedQuantity(run: KittingPickRun): number {
  return run.items.reduce((sum, item) => sum + Math.max(0, Math.trunc(item.pickedQuantity)), 0);
}

export function kittingRunActiveCartLineCount(run: KittingPickRun): number {
  return run.items.filter((item) => item.pickedQuantity > 0).length;
}
